import { Form, SubmitFormButton } from '@calendly/form'
import { useOnValueChange } from '@calendly/hooks'
import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { Loader } from '@calendly/ui/components/loader'
import { Body, Footer, Header, Popup } from '@calendly/ui/components/popup'
import { BodyText } from '@calendly/ui/components/typography'
import { useCallback, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import {
  selectById as selectCalendarConnectionById,
  useFetchCalendarAccountsQuery,
  usePullCalendarsLimit,
  useUpdateCalendarAccountMutation
} from 'features/calendar_connections'


import { Calendars } from './calendars'
import { OutlookDesktopInfo } from './outlook_desktop_info'
import { ModalContainer } from './styles'
import { UnavailableStatuses } from './unavailable_statuses'
import { CALENDAR_SETTINGS_PATH } from '../../../constants'

const PullCalendarsDescription = ({ provider }) => {
  const [isLimited, _isFieldDisabled, maxSubCalendars] = usePullCalendarsLimit(provider, 'pullCalendarIds')

  if (provider === 'google') {
    return (
      <BodyText>
        {HtmlParser(I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.google_description'))}
      </BodyText>
    )
  }

  return (
    <>
      <BodyText>
        {HtmlParser(I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.outlook_description'))}
      </BodyText>
      {isLimited && (
        <BodyText color="muted">
          {I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.limit', { count: maxSubCalendars })}
        </BodyText>
      )}
    </>
  )
}

export const PullPopup = () => {
  const history = useHistory()
  const { id } = useParams()
  const { data, isLoading, isSuccess: isLoaded } = useFetchCalendarAccountsQuery()
  const { blocked, kind, identifier, pullCalendarIds, unavailableStatuses } = useMemo(() => (
    isLoaded ? selectCalendarConnectionById(data, id) : {}
  ), [isLoaded, data, id])
  const [updateAccount, { isSuccess }] = useUpdateCalendarAccountMutation()
  const isOutlookDesktop = kind === 'outlook_desktop'

  useOnValueChange(isSuccess, () => {
    if (isSuccess) {
      history.push(CALENDAR_SETTINGS_PATH)
    }
  })

  const onSubmit = useCallback((values) => updateAccount({
    id,
    pullCalendarIds: values.pullCalendarIds,
    unavailableStatuses: values.unavailableStatuses
  }), [updateAccount, id])

  const onClose = () => history.push(CALENDAR_SETTINGS_PATH)

  return (
    <Popup onClose={onClose} withCloseButton>
      <Header
        title={I18n.t('publisher.availability_settings.section.calendar_settings.check_conflicts.popup.title')}
        eyebrowLabel={identifier}
      />
      {isLoading ? <Loader /> : (
        <Form
          initialValues={{ pullCalendarIds, unavailableStatuses }}
          onSubmit={onSubmit}
        >
          <Body>
            <ModalContainer>
              <PullCalendarsDescription provider={kind} />
              <Calendars />
              {isOutlookDesktop && <OutlookDesktopInfo id={id} blocked={blocked} />}
              <UnavailableStatuses />
            </ModalContainer>
          </Body>
          <Footer>
            <Button decoration="secondary-outline" onClick={onClose}>
              {I18n.t('publisher.action.cancel')}
            </Button>
            <SubmitFormButton>
              {I18n.t('publisher.action.update')}
            </SubmitFormButton>
          </Footer>
        </Form>
      )}
    </Popup>
  )
}
